<template>
  <v-container class="remove-mobile-container-padding">
    <div class="container-mobile-normal-padding">
      <v-row dense>
        <v-col cols="12" sm="6" md="4">
          <EventParticipantsStats
            :registrations="filtered"
            :discipline-name="disciplineName"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" sm="6">
          <div v-if="!singleList">
            <h2 style="font-size: 16px">
              {{
                $t(
                  "events:EventPage.participants.RegistrationsPerClass.SectionHeading",
                  "Registrations per class"
                )
              }}
            </h2>
          </div>
        </v-col>
        <v-col cols="12" sm="6">
          <div class="d-flex justify-center justify-sm-end">
            <v-btn color="primary" @click="() => (singleList = !singleList)">
              <v-icon color="text-white" size="small" start>
                $iconImage
              </v-icon>

              <template v-if="singleList">
                {{
                  $t(
                    "events:participants.ViewRegistrationsPerClass.button",
                    "View registrations per class"
                  )
                }}
              </template>
              <template v-else>
                {{
                  $t(
                    "events:participants.ViewSingleList.button",
                    "View registrations as single list"
                  )
                }}
              </template>
            </v-btn>
          </div>
        </v-col>
      </v-row>

      <slot name="registrationLoading" />
    </div>
    <template v-if="singleList">
      <AppFilterNavigation
        v-model:filters="filters"
        show-reset-public
        no-preserve
        class="mb-3"
        @reset-filter="() => resetFilter()"
      >
        <v-col cols="12" md="2">
          <AppSelect
            v-model="filters.sortBy"
            :input-props="{
              items: sortBys,
              label: $t('events:common.SortBy.label', 'Sort by')
            }"
          />
        </v-col>
        <v-col cols="12" md="2">
          <AppSelect
            v-model="filters.competitions"
            :input-props="{
              multiple: true,
              items: filterCompetitions,
              label: $t('events:common.Classes.label', 'Classes'),
              itemTitle: 'code',
              itemValue: 'code',
              multiple: true,
              clearable: true
            }"
            data-test="competitions"
          />
        </v-col>
        <v-col cols="12" md="2">
          <AppSelect
            v-model="filters.filterByClub"
            :input-props="{
              label: $t(
                'events:EventPage.participants.filterBy.club.label',
                'Club'
              ),
              items: clubs,
              itemTitle: 'name',
              itemValue: 'id',
              multiple: true,
              clearable: true
            }"
          />
        </v-col>
        <v-col cols="12" md="4">
          <AppSearchBox v-model="filters.search" clearable />
        </v-col>
      </AppFilterNavigation>

      <AppTableHeader>
        <template
          v-if="event.externalIdFrom == 'FEI' && event.lastSyncedAt"
          #left
        >
          <span class="font-weight-regular" data-test="lastSyncedAtFei">
            {{
              $t(
                "events:event.masterlist.lastFeiSyncTimestamp.text",
                "Last full update from FEI at:"
              )
            }}
            {{ $dayjs.date(event.lastSyncedAt, "fullTime") }}
          </span>
        </template>
      </AppTableHeader>

      <v-data-table
        :headers="headerTitles"
        :items="filtered"
        :hide-default-header="$vuetify.display.smAndDown"
        hide-default-footer
        disable-sort
        disable-filtering
        disable-pagination
        :items-per-page="-1"
        item-value="id"
        class="entsys--table keep-top"
      >
        <template #no-data>
          {{
            $t(
              "common:NoResults.messsage",
              "No data available",
              "No results message"
            )
          }}
        </template>

        <template #item="{ item }">
          <template v-if="$vuetify.display.mdAndUp">
            <tr
              :data-scroll-focus="'registration-' + item.id"
              :class="registrationStateColor(item)"
            >
              <template v-if="disciplineName === 'vaulting'">
                <td>
                  <div class="my-2">
                    {{ item.teamName }}
                  </div>
                </td>
                <td>
                  <div class="my-2">
                    <AppShowRegistrationPeople
                      component-name="AppShowAthleteName"
                      :people="item.athletes"
                      :registration-people="item.registrationPeople"
                    />
                  </div>
                </td>
              </template>
              <td>
                <div v-if="disciplineName === 'vaulting'" class="my-2">
                  <AppShowPersonName :person="item.vaultingLungerPerson" />
                </div>
                <div v-else class="my-2">
                  <AppShowRegistrationPeople
                    component-name="AppShowAthleteName"
                    :people="item.athletes"
                    :registration-people="item.registrationPeople"
                  />

                  <div v-if="disciplineName === 'driving'" class="mt-1">
                    {{
                      $t(
                        "events:EntryForm.driving.grooms.label",
                        "Groom(s)",
                        "Label for the grooms name field in entry form for driving"
                      )
                    }}
                    <AppShowDrivingGrooms :registration="item" />
                    <br />
                    {{
                      $t(
                        "events:EntryForm.driving.CarriageNumber.label",
                        "Carriage number",
                        "Label for the carriage number field in entry form for driving"
                      )
                    }}
                    {{ item.carriageNumber }}
                  </div>
                </div>
              </td>
              <td>
                <div class="my-2">
                  <AppShowRegistrationHorses
                    component-name="AppShowHorseName"
                    :horse-slot-props="{
                      badge: true
                    }"
                    :horses="item.horses"
                    :registration-horses="item.registrationHorses"
                    :component-props="{
                      showIdentifier: true
                    }"
                  />
                </div>
              </td>
              <td>
                <div class="text-secondary my-2">
                  <template v-if="item.entries.some(x => x.teamName)">
                    <div
                      v-for="(
                        { teamName, entries }, i
                      ) in groupedByTeamNameCompetitions(item.entries)"
                      :key="'team-row-' + String(i)"
                    >
                      {{ teamName }}
                      <AppShowEntryClassChip
                        v-for="entry in entries"
                        :key="entry.id"
                        :entry="entry"
                        :chip-props="{
                          classList: 'mr-1'
                        }"
                        :chiples-props="{
                          class: 'mr-1'
                        }"
                      >
                        {{ entry.competition.code }}
                      </AppShowEntryClassChip>
                    </div>
                  </template>
                  <template v-else>
                    <AppShowEntryClassChip
                      v-for="entry in sortEntriesByCompetitionCode(
                        item.entries
                      )"
                      :key="entry.id"
                      :entry="entry"
                      :chip-props="{
                        classList: 'mr-1'
                      }"
                      :chiples-props="{
                        class: 'mr-1'
                      }"
                    >
                      {{ entry.competition.code }}
                    </AppShowEntryClassChip>
                  </template>
                </div>
              </td>
              <td>
                <div class="my-2">
                  {{ $dayjs.date(item.createdAt, "fullTime") }}
                </div>
              </td>
              <td>
                <div class="my-2 actions-space">
                  <v-btn-icon
                    v-if="
                      $allowedMutation(
                        item.allowedMutations,
                        'registrationCreateOrUpdate'
                      )
                    "
                    :aria-label="$t('common:editButton.alt')"
                    :to="{
                      name: 'EventRegistrationEditPage',
                      params: {
                        eventId: event.id,
                        registrationId: item.id
                      },
                      state: {
                        returnRoute: {
                          name: 'EventSchedule',
                          hash: '#participants',
                          params: {
                            eventId: event.id
                          },
                          state: {
                            routeScrollState: {
                              querySelector: `[data-scroll-focus='registration-${item.id}']`
                            }
                          }
                        }
                      }
                    }"
                    size="small"
                    class="bg-edit-green"
                  >
                    <v-icon size="small" color="white">$edit</v-icon>
                  </v-btn-icon>
                </div>
              </td>
            </tr>
          </template>
          <template v-else>
            <tr
              :data-scroll-focus="'registration-' + item.id"
              :class="registrationStateColor(item)"
            >
              <td class="px-5">
                <div v-if="disciplineName === 'vaulting'">
                  <v-row no-gutters>
                    <v-col>
                      <b>
                        {{
                          $t(
                            "events:EntryForm.vaulting.teamnamme.label",
                            "Team name",
                            "Label for the team name field in entry form for vaulting"
                          )
                        }}
                      </b>
                      <br />
                      {{ item.teamName }}
                    </v-col>
                    <v-col v-if="item.vaultingLungerPerson">
                      <b>
                        {{
                          $t(
                            "events:EntryForm.vaulting.lunger.label",
                            "Lunger",
                            "Label for the lunger name field in entry form for vaulting"
                          )
                        }}
                      </b>
                      <br />
                      <AppShowAthleteName
                        :athlete="item.vaultingLungerPerson"
                      />
                    </v-col>
                  </v-row>

                  <v-divider class="my-3" />
                </div>
                <v-row no-gutters>
                  <v-col cols="10">
                    <div class="mb-2">
                      <b>
                        {{
                          $t(
                            "events:common.Athletes.label",
                            "Athlete",
                            "Label for athlete in different parts of the system"
                          )
                        }}
                      </b>
                      <AppShowRegistrationPeople
                        component-name="AppShowAthleteName"
                        :people="item.athletes"
                        :registration-people="item.registrationPeople"
                      />
                    </div>

                    <div>
                      <b>
                        {{
                          $t(
                            "events:common.Horses.label",
                            "Horses",
                            "Label for horses in different parts of the system"
                          )
                        }}
                      </b>
                      <AppShowRegistrationHorses
                        component-name="AppShowHorseName"
                        :horses="item.horses"
                        :horse-slot-props="{
                          badge: true
                        }"
                        :registration-horses="item.registrationHorses"
                        :component-props="{
                          showIdentifier: true
                        }"
                      />
                    </div>

                    <div class="my-1">
                      <strong>{{
                        $t("events:common.RegisteredAt.label", "Registered at")
                      }}</strong>

                      <div>
                        {{ $dayjs.date(item.createdAt, "fullTime") }}
                      </div>
                    </div>
                  </v-col>

                  <v-col
                    v-if="
                      $allowedMutation(item.allowedMutations, [
                        'registrationCreateOrUpdate',
                        'registrationUnregister'
                      ])
                    "
                    cols="2"
                    style="max-height: 50%"
                    class="d-flex flex-column justify-space-between"
                  >
                    <div
                      class="text-right"
                      style="
                        display: flex;
                        flex-direction: column;
                        align-items: flex-end;
                      "
                    >
                      <v-btn-icon
                        v-if="
                          $allowedMutation(
                            item.allowedMutations,
                            'registrationCreateOrUpdate'
                          )
                        "
                        size="small"
                        :aria-label="$t('common:editButton.alt')"
                        :to="{
                          name: 'EventRegistrationEditPage',
                          params: {
                            eventId: event.id,
                            registrationId: item.id
                          },
                          state: {
                            returnRoute: {
                              name: 'EventSchedule',
                              hash: '#participants',
                              params: {
                                eventId: event.id
                              },
                              state: {
                                routeScrollState: {
                                  querySelector: `[data-scroll-focus='registration-${item.id}']`
                                }
                              }
                            }
                          }
                        }"
                        icon
                        class="bg-edit-green"
                      >
                        <v-icon size="small" color="white"> $edit </v-icon>
                      </v-btn-icon>
                    </div>
                  </v-col>
                </v-row>
                <template v-if="disciplineName === 'driving'">
                  <v-divider class="my-3" />
                  <div>
                    {{
                      $t(
                        "events:EntryForm.driving.grooms.label",
                        "Groom(s)",
                        "Label for the grooms name field in entry form for driving"
                      )
                    }}
                    <AppShowDrivingGrooms :registration="item" />
                    <br />
                    {{
                      $t(
                        "events:EntryForm.driving.CarriageNumber.label",
                        "Carriage number",
                        "Label for the carriage number field in entry form for driving"
                      )
                    }}
                    {{ item.carriageNumber }}
                  </div>
                </template>

                <v-divider class="my-3" />

                <div>
                  <b>
                    {{
                      $t(
                        "events:common.Classes.label",
                        "Classes",
                        "Label for classes in different parts of the system"
                      )
                    }}
                  </b>
                  <br />
                  <span class="text-secondary">
                    <template v-if="item.entries.some(x => x.teamName)">
                      <div
                        v-for="(
                          { teamName, entries }, i
                        ) in groupedByTeamNameCompetitions(item.entries)"
                        :key="'team-row-' + String(i)"
                      >
                        {{ teamName }}
                        <AppShowEntryClassChip
                          v-for="entry in entries"
                          :key="entry.id"
                          :entry="entry"
                          :chip-props="{
                            classList: 'mr-1'
                          }"
                          :chiples-props="{
                            class: 'mr-1'
                          }"
                          >{{ entry.competition.code }}</AppShowEntryClassChip
                        >
                      </div>
                    </template>
                    <template v-else>
                      <AppShowEntryClassChip
                        v-for="entry in sortEntriesByCompetitionCode(
                          item.entries
                        )"
                        :key="entry.id"
                        :entry="entry"
                        :chip-props="{
                          classList: 'mr-1'
                        }"
                        :chiples-props="{
                          class: 'mr-1'
                        }"
                        >{{ entry.competition.code }}</AppShowEntryClassChip
                      >
                    </template>
                  </span>
                  <br />
                  {{ $t("events:common.RegisteredAt.label", "Registered at") }}
                  <br />
                  {{ $dayjs.date(item.createdAt, "fullTime") }}
                </div>
              </td>
            </tr>
          </template>
        </template>
      </v-data-table>
    </template>
    <div v-else>
      <div
        v-if="
          !eventResultsPublished && competitionsOnlyRegistrations.length > 0
        "
      >
        <AppTableHeader>
          <template
            v-if="event.externalIdFrom == 'FEI' && event.lastSyncedAt"
            #left
          >
            <span class="font-weight-regular" data-test="lastSyncedAtFei">
              {{
                $t(
                  "events:event.masterlist.lastFeiSyncTimestamp.text",
                  "Last full update from FEI at:"
                )
              }}
              {{ $dayjs.date(event.lastSyncedAt, "fullTime") }}
            </span>
          </template>
        </AppTableHeader>
        <EventParticipantsByClass
          v-model:competitions-expanded="competitionsExpanded"
          only-for-registrations-mode
          v-bind="{
            competitions: competitionsOnlyRegistrations,
            event,
            headers: forRegistrationOnlyTitles,
            disciplineName,
            registrations
          }"
          class="mb-8"
        />
      </div>

      <div v-for="(competitionDay, i) in competitionDays" :key="i" class="mb-8">
        <template v-if="competitionsOnDay(competitionDay).length > 0">
          <AppTableHeader>
            <template #left>
              {{ $dayjs.date(competitionDay, "formal") }}
            </template>
          </AppTableHeader>

          <EventParticipantsByClass
            v-model:competitions-expanded="competitionsExpanded"
            v-bind="{
              competitions: competitionsOnDay(competitionDay),
              event,
              headers: compTitles,
              disciplineName,
              registrations
            }"
          />
        </template>
      </div>
    </div>
  </v-container>
</template>
<script>
import { defineComponent, ref, toRef } from "vue";
import {
  groupBy,
  sortBy,
  uniqBy,
  orderBy,
  get,
  clone,
  cloneDeep
} from "lodash-es";
import gql from "fraql";
import { getFederationConfiguration } from "@composables/masterdata/config";
import { useParticipantsHelper } from "./mixin";
import { useCompetitionHelpers } from "@composables/competition_helpers";
import calculateCompetitionDays from "@/utils/competitionDays";

import EventParticipantsStats from "@/components/event/EventSchedule/EventParticipantsStats.vue";
import AppFilterNavigation from "@elements/AppFilterNavigation.vue";
import AppSelect from "@AppSelects/AppSelect.vue";

import AppShowPersonName from "@AppShows/person/AppShowPersonName.vue";
import AppShowAthleteName from "@AppShows/person/AppShowAthleteName.vue";
import AppShowRegistrationHorses from "@AppShows/horse/AppShowRegistrationHorses.vue";
import AppShowRegistrationPeople from "@AppShows/person/AppShowRegistrationPeople.vue";
import AppShowDifficultyLevel from "@AppShows/AppShowDifficultyLevel.vue";
import AppShowClassRegistrationsCount from "@AppShows/event/AppShowClassRegistrationsCount.vue";
import AppShowCompetitionGrandPrix from "@AppShows/event/AppShowCompetitionGrandPrix.vue";

import EventParticipantsByClass from "./EventParticipantsByClass.vue";
import AppShowEntryClassChip from "@AppShows/event/AppShowEntryClassChip.vue";
import AppTableHeader from "@/components/elements/table/AppTableHeader.vue";
import AppShowDrivingGrooms from "@AppShows/event/AppShowDrivingGrooms.vue";
import AppSearchBox from "@/components/elements/AppSearchBox.vue";

import * as JsSearch from "js-search";

import { registrationsAthleteNameSort } from "@/utils/personNameSort";

export default defineComponent({
  name: "EventParticipantsTemplate",
  components: {
    AppShowAthleteName,
    EventParticipantsStats,
    AppShowRegistrationHorses,
    AppShowRegistrationPeople,
    AppSelect,
    AppFilterNavigation,
    EventParticipantsByClass,
    AppTableHeader,
    AppShowEntryClassChip,
    AppShowDrivingGrooms,
    AppSearchBox,
    AppShowPersonName
  },
  props: {
    event: {
      type: Object,
      required: true
    },
    registrations: {
      type: Array,
      required: true
    },
    expanded: {
      type: Array,
      required: true
    },
    disciplineName: {
      type: String,
      required: true
    }
  },
  emits: ["update:expanded"],
  setup(props) {
    const event = toRef(props, "event");
    const { registrationStateColor, filterEntriesOutByResolution } =
      useParticipantsHelper(event);

    const compHelpers = useCompetitionHelpers();

    const singleList = ref(false);
    const filters = ref({
      search: null,
      sortBy: "createdAt",
      competitions: [],
      filterByClub: []
    });

    return {
      ...compHelpers,
      ...getFederationConfiguration(),
      get,
      singleList,
      filters,

      filterEntriesOutByResolution,
      registrationStateColor
    };
  },
  computed: {
    competitionsExpanded: {
      get() {
        return this.expanded;
      },
      set(v) {
        this.$emit("update:expanded", v);
      }
    },
    eventResultsPublished() {
      return this.event.status == "results_published";
    },
    forRegistrationOnlyTitles() {
      return [
        {
          value: "x__",
          title: this.$t(
            "events:EventPage.registrationCompetitionsSection.registrationCompetition.column",
            "Event"
          ),
          sortable: false
        },
        {
          value: "x",
          title: this.$t(
            "events:EventPage.registrationCompetitionsSection.level.column",
            "Level"
          ),
          sortable: false
        },
        {
          value: "xp",
          title: this.$t(
            "events:EventPage.registrationCompetitionsSection.openFor.column",
            "Open for"
          ),
          sortable: false,
          align: "center"
        },
        {
          value: "xp",
          title: this.$t(
            "events:EventPage.registrationCompetitionsSection.numberOfEntries.column",
            "Number of entries"
          ),
          sortable: false,
          align: "center"
        },
        {
          value: "__",
          title: "",
          sortable: false,
          align: "center"
        }
      ];
    },
    compTitles() {
      return [
        {
          value: "x__",
          title: this.$t(
            "events:EventPage.CompetitionsSection.competitions.column",
            "Competition",
            "Competition column label in competitions section on event page"
          ),
          sortable: false
        },
        {
          value: "x",
          title: this.$t(
            "events:EventPage.CompetitionsSection.difficulty.column",
            "Difficulty",
            "Difficulty column label in competitions section on event page"
          ),
          sortable: false
        },
        {
          value: "x_",
          title: this.$t(
            "events:EventPage.CompetitionsSection.judgement.column",
            "Judgement",
            "Judgement column label in competitions section on event page"
          ),
          sortable: false
        },
        {
          value: "xp",
          title: this.$t(
            "events:EventPage.CompetitionsSection.registered.column",
            "Registered",
            "Registered column label in competitions section on event page"
          ),
          sortable: false,
          align: "center"
        },
        {
          value: "__",
          title: "",
          sortable: false,
          align: "center"
        }
      ];
    },
    sortedCompetitions() {
      const sorted = this.competitionsOrdered(
        JSON.parse(JSON.stringify(this.event.competitions))
      );

      if (this.eventResultsPublished) {
        return sorted.filter(c => c.forRegistrationOnly == false);
      }

      return sorted;
    },
    filterCompetitions() {
      const sorter = this.competitionOrderFn;
      const sorted = this.sortedCompetitions;

      return sorted.sort((a, b) =>
        a.forRegistrationOnly
          ? 1
          : a.forRegistrationOnly == b.forRegistrationOnly
            ? sorter(a.code, b.code)
            : -1
      );
    },
    competitionsOnlyRegistrations() {
      return this.sortedCompetitions.filter(c => c.forRegistrationOnly == true);
    },
    normalCompetitions() {
      return this.sortedCompetitions.filter(c => !c.forRegistrationOnly);
    },
    sortBys() {
      return [
        {
          value: "createdAt",
          text: this.$t("events:common.RegisteredAt.label", "Registered at")
        },
        {
          value: "athlete",
          text: this.$t("events:common.Athletes.label", "Athletes")
        },
        {
          value: "horse",
          text: this.$t("events:common.Horses.label", "Horses")
        },
        {
          value: "club",
          text: this.$t(
            "events:EventPage.participants.sortBy.club.label",
            "Club"
          )
        }
      ];
    },
    filtered() {
      let registrations = cloneDeep(this.registrations);

      if (this.eventResultsPublished) {
        registrations = registrations.filter(registration => {
          registration.entries = registration.entries.filter(
            entry => !entry.competition.forRegistrationOnly
          );
          return registration.entries.length;
        });
        registrations = this.filterEntriesOutByResolution(registrations);
      }

      switch (this.filters.sortBy) {
        case "createdAt":
          registrations = orderBy(registrations, "createdAt", "desc");
          break;
        case "athlete":
          registrations = registrationsAthleteNameSort(registrations);
          break;
        case "horse":
          registrations = sortBy(registrations, i => i.horses[0].name);
          break;
        case "club":
          registrations = sortBy(
            registrations,
            item => item.registrationPeople[0]?.personClubOrganisation?.name
          );
          break;
      }

      if (this.filters.competitions.length) {
        registrations = registrations.filter(registration => {
          registration.entries = registration.entries.filter(entry => {
            if (this.filters.competitions.includes(entry.competition.code))
              return entry;
          });
          if (registration.entries.length) return registration;
        });
      }

      if (this.filters.filterByClub.length) {
        registrations = registrations.filter(item =>
          this.filters.filterByClub.includes(
            item.registrationPeople[0]?.personClubOrganisationId
          )
        );
      }

      if (this.filters.search) {
        // js-search doesn't support searching nested array of objects
        // nor does minisearch looking at its readme
        registrations = registrations.map(r => {
          r.athleteNames = r.athletes?.map(a => a.name) ?? "";
          r.horseNames = r.horses?.map(a => a.name) ?? "";
          return r;
        });

        const s = new JsSearch.Search("id");
        s.addIndex("athleteNames");
        s.addIndex("horseNames");
        s.addDocuments(registrations);
        registrations = s.search(this.filters.search);
      }
      return registrations;
    },
    registrationStats() {
      if (this.eventResultsPublished) {
        return this.filtered.reduce((regs, registration) => {
          registration = clone(registration);
          registration.entries = registration.entries.filter(
            entry => !entry.competition.forRegistrationOnly // Exclude these from statistic
          );
          if (registration.entries.length) {
            regs.push(registration);
          }
          return regs;
        }, []);
      }
      return this.filtered;
    },
    pairs() {
      return Object.values(
        groupBy(
          this.entries,
          entry =>
            entry.athletes.map(a => a.id) + entry.horses.map(h => h.id).join("")
        )
      );
    },
    competitionDays() {
      return calculateCompetitionDays(this.event.startDate, this.event.endDate);
    },
    headerTitles() {
      if (this.disciplineName === "vaulting") {
        return [
          {
            value: "team",
            title: this.$t(
              "events:EntryForm.vaulting.teamnamme.label",
              "Team name",
              "Label for the team name field in entry form for vaulting"
            ),
            sortable: false
          },
          {
            value: "athletes",
            title: this.$t(
              "events:common.athlete.label",
              "Athlete",
              "Label for athlete in different parts of the system"
            ),
            sortable: false
          },
          {
            value: "lunger",
            title: this.$t(
              "events:EntryForm.vaulting.lunger.label",
              "Lunger",
              "Label for the lunger name field in entry form for vaulting"
            ),
            sortable: false
          },
          {
            value: "horse",
            title: this.$t(
              "events:common.Horse.label",
              "Horse",
              "Label for horse in different parts of the system"
            ),
            sortable: false
          },
          {
            value: "class",
            title: this.$t(
              "events:common.Classes.label",
              "classes",
              "Label for classes in different parts of the system"
            ),
            sortable: false
          },
          {
            value: "reg",
            title: this.$t("events:common.RegisteredAt.label", "Registered at"),
            sortable: false
          },
          { value: "actions", title: "", sortable: false }
        ];
      } else {
        return [
          {
            value: "athelete",
            title: this.$t(
              "events:common.athlete.label",
              "Athlete",
              "Label for athlete in different parts of the system"
            ),
            sortable: false
          },
          {
            value: "horse",
            title: this.$t(
              "events:common.Horse.label",
              "Horse",
              "Label for horse in different parts of the system"
            ),
            sortable: false
          },
          {
            value: "class",
            title: this.$t(
              "events:common.Classes.label",
              "Classes",
              "Label for classes in different parts of the system"
            ),
            sortable: false
          },
          {
            value: "reg",
            title: this.$t("events:common.RegisteredAt.label", "Registered at"),
            sortable: false
          },
          { value: "actions", title: "", sortable: false }
        ];
      }
    },
    clubs() {
      return sortBy(
        uniqBy(
          this.registrations.reduce((x, y) => {
            if (y.registrationPeople[0]?.personClubOrganisation) {
              x.push(y.registrationPeople[0].personClubOrganisation);
            }
            return x;
          }, []),
          "name"
        ),
        "name"
      );
    }
  },
  updated() {
    if (!this.event?.id) return;
    sessionStorage.setItem(
      `${this.event.id.split("-").join("")}`,
      JSON.stringify({
        participants: {
          singleList: this.singleList,
          competitionsExpanded: this.competitionsExpanded
        }
      })
    );
  },
  mounted() {
    if (this.preferences && this.preferences.singleList)
      this.singleList = this.preferences.singleList;
  },
  methods: {
    resetFilter() {
      this.filters = {
        search: null,
        sortBy: "createdAt",
        competitions: [],
        filterByClub: []
      };
    },
    groupedByTeamNameCompetitions(entries) {
      return Object.entries(
        groupBy(entries, function (e) {
          if (e.teamName) return e.teamName;
          return "FIPA-NO-TEAM";
        })
      ).map(([teamName, entries]) => {
        return {
          teamName: teamName != "FIPA-NO-TEAM" ? teamName : "",
          entries: this.sortEntriesByCompetitionCode(entries)
        };
      });
    },

    competitionsOnDay(day) {
      return this.competitionsOrdered(
        this.normalCompetitions.filter(
          comp =>
            this.$dayjs(day).isSame(comp.startDate) && !comp.forRegistrationOnly
        )
      );
    },

    sortEntriesByCompetitionCode(entries) {
      const sorter = this.competitionOrderFn;
      return clone(entries).sort((a, b) =>
        sorter(a.competition.code, b.competition.code)
      );
    }
  },
  fragments: {
    eventFields: gql`
      fragment _ on Event {
        externalIdFrom
        lastSyncedAt
      }
    `,
    registrationFields: gql`
      fragment _ on Registration {
        ${AppShowDrivingGrooms.fragments.registrationFields}
      }
    `,
    registrationHorse: AppShowRegistrationHorses.fragments.registrationHorse,
    registrationPerson: AppShowRegistrationPeople.fragments.registrationPerson,
    horseFields: AppShowRegistrationHorses.fragments.horse,
    personFields: AppShowRegistrationPeople.fragments.athlete,
    difficultyLevelFields:
      AppShowDifficultyLevel.fragments.difficultyLevelFields,
    grandPrix: AppShowCompetitionGrandPrix.fragments.grandPrix,
    restriction: AppShowClassRegistrationsCount.fragments.restriction,
    entryFields: gql`
      fragment _ on Entry {
        ${EventParticipantsByClass.fragments.entryFields}
        ${AppShowEntryClassChip.fragments.entryFields}
      }
    `,
    competitionFields: gql`
      fragment _ on Competition {
        id
        code
        forRegistrationOnly
        ${EventParticipantsByClass.fragments.competitionFields}
      }
    `
  }
});
</script>
